import type { ReactNode } from "react"
import { BlockPendingOverlay } from "@/components/shell/block-pending-overlay"
import { ReloadBoundary } from "@/components/shell/reload-boundary"
import { cn } from "@/lib/utils"

type ShotColumnStageProps<T extends string> = {
  shownId: T
  selectedId: T
  /** Mount the incoming screen hidden so its fetch starts while the previous one stays visible. */
  preloadIncoming: boolean
  label: string
  className?: string
  slotClassName?: string
  render: (id: T) => ReactNode
}

/**
 * Keeps the visible screen mounted under the overlay. A preloaded incoming screen stays in the tree
 * when it becomes current, so its fetch is not started twice. A screen that fails to render offers a reload
 * in its own column instead of blanking the whole panel.
 */
export function ShotColumnStage<T extends string>({
  shownId,
  selectedId,
  preloadIncoming,
  label,
  className,
  slotClassName,
  render,
}: ShotColumnStageProps<T>) {
  const pending = shownId !== selectedId
  return (
    <div className={cn("relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden", className)}>
      <div key={shownId} className={cn("flex min-h-0 min-w-0 flex-1 flex-col", slotClassName)}>
        <ReloadBoundary variant="block">{render(shownId)}</ReloadBoundary>
      </div>
      {pending && preloadIncoming ? (
        <div key={selectedId} className="hidden" aria-hidden>
          <ReloadBoundary variant="block">{render(selectedId)}</ReloadBoundary>
        </div>
      ) : null}
      {pending ? <BlockPendingOverlay label={label} /> : null}
    </div>
  )
}
